import { useMemo } from "react";
import { CalendarEvent, EVENT_TYPE_CONFIG } from "@/hooks/useCalendarEvents";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarEventPopover } from "./CalendarEventPopover";
import { cn } from "@/lib/utils";
import { format, startOfWeek, endOfWeek, addWeeks, isAfter } from "date-fns";

interface Props {
  selectedDate: Date;
  events: CalendarEvent[];
  layers: Record<string, boolean>;
}

export const CalendarListView = ({ selectedDate, events, layers }: Props) => {
  const filtered = events.filter((e) => layers[e.event_type] !== false);

  const grouped = useMemo(() => {
    const now = new Date(selectedDate);
    const weeks: { label: string; start: Date; end: Date; events: CalendarEvent[] }[] = [];

    for (let i = 0; i < 6; i++) {
      const weekDate = addWeeks(now, i);
      const ws = startOfWeek(weekDate);
      const we = endOfWeek(weekDate);
      const label =
        i === 0
          ? `This Week — ${format(ws, "MMM d")}–${format(we, "d")}`
          : i === 1
          ? `Next Week — ${format(ws, "MMM d")}–${format(we, "d")}`
          : `${format(ws, "MMM d")}–${format(we, "d")}`;
      weeks.push({ label, start: ws, end: we, events: [] });
    }

    filtered.forEach((e) => {
      const eDate = new Date(e.start_date + "T00:00:00");
      for (const w of weeks) {
        if (eDate >= w.start && eDate <= w.end) {
          w.events.push(e);
          break;
        }
      }
    });

    return weeks.filter((w) => w.events.length > 0);
  }, [filtered, selectedDate]);

  if (grouped.length === 0) {
    return (
      <div className="flex-1 p-8 text-center text-muted-foreground">
        No upcoming events in the next 6 weeks.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4 space-y-6">
      {grouped.map((week) => (
        <div key={week.label}>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {week.label}
          </h3>
          <div className="space-y-1">
            {week.events
              .sort((a, b) => a.start_date.localeCompare(b.start_date))
              .map((ev) => {
                const cfg = EVENT_TYPE_CONFIG[ev.event_type];
                return (
                  <Popover key={ev.id}>
                    <PopoverTrigger asChild>
                      <button
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-2 rounded-md text-left border-l-2 transition-colors hover:bg-muted/50",
                          cfg.bgClass,
                          cfg.borderClass
                        )}
                      >
                        <span className="text-xs text-muted-foreground w-16 shrink-0">
                          {format(new Date(ev.start_date + "T00:00:00"), "MMM d")}
                        </span>
                        <span className={cn("h-2 w-2 rounded-full shrink-0")} style={{ backgroundColor: cfg.color }} />
                        <span className={cn("text-sm font-medium truncate flex-1", cfg.textClass)}>
                          {ev.title}
                          {ev.start_time && !ev.all_day && (
                            <span className="text-muted-foreground ml-2 text-xs">
                              · {ev.start_time.slice(0, 5)}
                            </span>
                          )}
                        </span>
                        <span className="text-[10px] text-muted-foreground shrink-0">{cfg.label}</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-0">
                      <CalendarEventPopover event={ev} />
                    </PopoverContent>
                  </Popover>
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
};
